import React, { useState, useEffect } from 'react'
import { Container, ModalContent } from './styled'
import { useTranslation } from 'react-i18next'
import {
  Grid,
  TextField,
  Button,
  Modal,
  Typography,
  Box,
  IconButton,
  Drawer,
  Snackbar,
  Alert,
  MenuItem,
  Fab,
  Checkbox,
  FormControlLabel
} from '@mui/material'
import StaffListContainer from './staffListContainer'
import StaffDetail from '../staffDetail/view'
import FilterListIcon from '@mui/icons-material/FilterList'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import { getStaffMembers, createStaffMember, getVenues, resendActivationEmail } from 'api'

const StaffList = ({ clientId }) => {
  const { t } = useTranslation()
  const [staffMembers, setStaffMembers] = useState([])
  const [openAddModal, setOpenAddModal] = useState(false)
  const [openDetails, setOpenDetails] = useState(false)
  const [selectedStaff, setSelectedStaff] = useState(null)
  const [staffLoaded, setStaffLoaded] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [venues, setVenues] = useState([])
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'MA', // Default to Manager,
    sendEmail: true // Add this new field, checked by default
  })

  const [filters, setFilters] = useState({
    role: 'AL',
    status: 'AL',
    search: '',
    order_by: 'create_date',
    order_dir: 'desc',
    page_size: 10,
    page_number: 1
  })

  const [paginationInfo, setPaginationInfo] = useState({
    page_number: 1,
    page_size: 10,
    page_total: 1,
    results_total: 0
  })

  const roleOptions = [
    { value: 'AL', label: 'All Roles' },
    { value: 'PA', label: 'Partner' },
    { value: 'AD', label: 'Admin' },
    { value: 'MA', label: 'Manager' }
  ]

  const statusOptions = [
    { value: 'AL', label: 'All Status' },
    { value: 'AC', label: 'Active' },
    { value: 'PP', label: 'Pending' },
    { value: 'IN', label: 'Inactive' }
  ]

  const roleMapping = {
    AL: 'All Roles',
    PA: 'Partner',
    AD: 'Admin',
    MA: 'Manager'
  }

  const statusMapping = {
    AL: 'All Status',
    AC: 'Active',
    PP: 'Pending',
    IN: 'Inactive'
  }

  function getVenuesInfo() {
    getVenues().then(r => {
      setVenues(r.venues)
    })
      .catch(console.error)
  }

  const refreshStaffList = () => {
    fetchStaffMembers();
    setOpenDetails(false);
  }

  // Function to fetch staff members
  const fetchStaffMembers = () => {
    setStaffLoaded(false)
    getStaffMembers(filters)
      .then(response => {
        setStaffMembers(response.staff)
        setStaffLoaded(true)
        setPaginationInfo({
          page_number: parseInt(response.page_number),
          page_size: parseInt(response.page_size),
          page_total: parseInt(response.page_total),
          results_total: parseInt(response.results_total)
        })
      })
      .catch(error => {
        setStaffLoaded(true)
        // Fallback to empty data on error
        setStaffMembers([])
        setPaginationInfo({
          page_number: 1,
          page_size: 10,
          page_total: 0,
          results_total: 0
        })
      })
  }

  const handleSortChange = (column) => {
    const newOrderDir = filters.order_by === column && filters.order_dir === 'asc' ? 'desc' : 'asc'
    setFilters({
      ...filters,
      order_by: column,
      order_dir: newOrderDir,
      page_number: 1
    })
  }

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
      page_number: 1
    })
  }

  const handlePageChange = (pageNumber) => {
    setFilters({
      ...filters,
      page_number: pageNumber
    })
  }

  const clearFilters = () => {
    setFilters({
      role: 'AL',
      status: 'AL',
      search: '',
      order_by: 'create_date',
      order_dir: 'desc',
      page_size: 10,
      page_number: 1
    })
  }

  const navigateToStaffDetail = (staff) => {
    setSelectedStaff(staff)
    setOpenDetails(true)
  }

  const handleSubmitStaff = () => {
    // This would be replaced with your actual API call to create a staff member
    // After successful creation, close modal and refresh list
    setOpenAddModal(false)
    // Reset form data
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      role: 'MA'
    })
    createStaffMember(formData)
      .then(() => {
        // Show success message
        setOpenAddModal(false)
        setFormData({
          email: '',
          first_name: '',
          last_name: '',
          role: 'MA',
          send_email: formData.sendEmail // Include this in API request
        })
        fetchStaffMembers()
      })
      .catch(error => {
        console.error('Error creating staff member:', error)
        // Show error message
        setOpenAddModal(false)
        setFormData({
          email: '',
          first_name: '',
          last_name: '',
          role: 'MA',
          send_email: true // Include this in API request
        })
        fetchStaffMembers()
      })
  }

  const handleResendActivation = async (slug) => {
    try {
      // Using async/await pattern instead of promise chains
      const response = await resendActivationEmail(slug);
      // Show success message if desired
    } catch (error) {
      console.error('Error sending activation email:', error);
      // Show error message if desired
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchStaffMembers()
    getVenuesInfo(true)
  }, [])

  // Fetch when filters change
  useEffect(() => {
    fetchStaffMembers()
  }, [filters])

  return (
    <Container sx={{ position: 'relative' }}>
      {/* Mobile Filter Button */}
      <Box
        onClick={() => setDrawerOpen(true)}
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed',
          right: 0,
          top: '33%',
          transform: 'translateY(-50%) translateX(0)',
          backgroundColor: 'primary.main',
          color: 'white',
          padding: '12px 8px',
          borderRadius: '8px 0 0 8px',
          cursor: 'pointer',
          alignItems: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'transform 0.3s ease',
          '&:hover': {
            transform: 'translateY(-50%) translateX(-4px)',
            backgroundColor: 'primary.dark',
          },
          zIndex: 1000,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            transform: 'rotate(-90deg)',
          }}
        >
          <FilterListIcon
            sx={{
              fontSize: '20px',
              transform: 'rotate(90deg)'
            }}
          />
        </Box>
      </Box>

      {/* Mobile Filter Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        transitionDuration={{ enter: 400, exit: 300 }}
      >
        <Box
          sx={{
            padding: 3,
            width: 300,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'background.default'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 3,
              pb: 2,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Typography variant="h6">{t('Filters')}</Typography>
            <IconButton
              onClick={() => setDrawerOpen(false)}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Filter Groups */}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {t('Role')}
              </Typography>
              <TextField
                select
                fullWidth
                name="role"
                value={filters.role}
                onChange={handleFilterChange}
                variant="outlined"
                size="small"
              >
                {roleOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {t('Status')}
              </Typography>
              <TextField
                select
                fullWidth
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                variant="outlined"
                size="small"
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {t('Search')}
              </Typography>
              <TextField
                fullWidth
                label={t('Name or Email')}
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                variant="outlined"
                size="small"
              />
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {t('Sort Options')}
              </Typography>
              <TextField
                select
                fullWidth
                name="order_by"
                value={filters.order_by}
                onChange={handleFilterChange}
                variant="outlined"
                size="small"
              >
                <MenuItem value="create_date">{t('Date Added')}</MenuItem>
                <MenuItem value="user__last_name">{t('Last Name')}</MenuItem>
                <MenuItem value="role">{t('Role')}</MenuItem>
                <MenuItem value="status">{t('Status')}</MenuItem>
              </TextField>
            </Box>
          </Box>

          {/* Footer Actions */}
          <Box
            sx={{
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              gap: 2
            }}
          >
            <Button
              variant="outlined"
              onClick={clearFilters}
              fullWidth
            >
              {t('Clear All')}
            </Button>
            <Button
              variant="contained"
              onClick={() => setDrawerOpen(false)}
              fullWidth
            >
              {t('Apply')}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Desktop Filters */}
      <Grid container spacing={2} sx={{ display: { xs: 'none', md: 'flex' }, padding: '5px 25px' }}>
        <Grid item xs={3}>
          <TextField
            select
            label={t('Role')}
            name="role"
            value={filters.role}
            onChange={handleFilterChange}
            variant="outlined"
            fullWidth
          >
            {roleOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.label)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={3}>
          <TextField
            select
            label={t('Status')}
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            variant="outlined"
            fullWidth
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.label)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={5}>
          <TextField
            label={t('Search by name or email')}
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
            variant="outlined"
            fullWidth
          />
        </Grid>
        <Grid item xs={1}>
          <Button
            variant="outlined"
            size="small"
            color="secondary"
            onClick={clearFilters}
            sx={{
              minWidth: 'auto',
              height: '100%',
              borderRadius: '20px'
            }}
          >
            {t('Clear')}
          </Button>
        </Grid>
      </Grid>

      {/* Staff List */}
      <StaffListContainer
        staffMembers={staffMembers}
        filters={filters}
        handleSortChange={handleSortChange}
        handlePageChange={handlePageChange}
        navigateToStaffDetail={navigateToStaffDetail}
        roleMapping={roleMapping}
        statusMapping={statusMapping}
        paginationInfo={paginationInfo}
        staffLoaded={staffLoaded}
        t={t}
      />

      {/* Add Staff FAB */}
      <Fab
        color="secondary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => setOpenAddModal(true)}
      >
        <AddIcon />
      </Fab>

      {/* Add Staff Modal */}
      <Modal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        aria-labelledby="add-staff-modal-title"
        aria-describedby="add-staff-modal-description"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: { xs: 2, sm: 4 }
        }}
      >
        <Box
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: { xs: 3, sm: 4 },
            maxWidth: '500px',
            width: '100%',
            position: 'relative'
          }}
        >
          <IconButton
            onClick={() => setOpenAddModal(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary'
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Typography
            id="add-staff-modal-title"
            variant="h5"
            component="h2"
            sx={{ fontWeight: 500, mb: 3 }}
          >
            {t('Add New Staff Member')}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                required
                label={t('Email')}
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t('First Name')}
                name="first_name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t('Last Name')}
                name="last_name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label={t('Role')}
                name="role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              >
                {roleOptions.filter(option => option.value !== 'AL').map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.sendEmail}
                  onChange={(e) => setFormData({ ...formData, sendEmail: e.target.checked })}
                  color="primary"
                />
              }
              label={t('Send invitation email')}
            />
          </Grid>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 4
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setOpenAddModal(false)}
              sx={{ borderRadius: '20px' }}
            >
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitStaff}
              color="primary"
              sx={{ borderRadius: '20px' }}
              disabled={!formData.email}
            >
              {t('Create Staff Member')}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Staff Detail Modal */}
      <Modal
        open={openDetails}
        onClose={() => setOpenDetails(false)}
        aria-labelledby="staff-detail-modal-title"
        aria-describedby="staff-detail-modal-description"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ModalContent width="80%">
          <StaffDetail
            staff={selectedStaff}
            venues={venues}
            onClose={() => setOpenDetails(false)}
            onUpdate={refreshStaffList}
            onResendActivation={handleResendActivation}
          />
        </ModalContent>
      </Modal>
    </Container>
  )
}

export default StaffList